import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useSuggestionsStore } from '@/hooks/useStores';

import './profile-pending-suggestions-banner.css';

interface ProfilePendingSuggestionsBannerProps {
  familyId: string;
  familyMemberId: string;
}

const ProfilePendingSuggestionsBanner: React.FC<ProfilePendingSuggestionsBannerProps> = observer(
  ({ familyId, familyMemberId }) => {
    const { t } = useTranslation('screens');
    const suggestionsStore = useSuggestionsStore();

    const load = useCallback(async () => {
      await suggestionsStore.loadPendingForMember(familyId, familyMemberId);
    }, [familyId, familyMemberId, suggestionsStore]);

    useEffect(() => {
      void load();
    }, [load]);

    const isForMember = suggestionsStore.memberPendingFamilyMemberId === familyMemberId;
    const count = isForMember ? suggestionsStore.memberPendingTotal : 0;
    const hasChangesRequested = isForMember && suggestionsStore.memberPendingHasChangesRequested;

    if (suggestionsStore.isLoadingMemberPending && !isForMember) {
      return null;
    }

    if (count <= 0) return null;

    const label = hasChangesRequested
      ? t('pendingSuggestionsBanner.needsChanges', { count })
      : t('pendingSuggestionsBanner.awaitingReview', { count });

    return (
      <div className="profile-pending-suggestions-banner" role="status">
        <p className="profile-pending-suggestions-banner__text">{label}</p>
        <Link
          className="profile-pending-suggestions-banner__link"
          to={`/families/${familyId}/suggestions?targetId=${encodeURIComponent(familyMemberId)}`}
        >
          {t('pendingSuggestionsBanner.viewMySuggestions')}
        </Link>
      </div>
    );
  }
);

export default ProfilePendingSuggestionsBanner;
